import * as React from 'react';
import { connect } from 'react-redux';
import { filter, find, flow, get, map, startCase } from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { II18nMessages } from '../../../../services/branding/models';
import {
  IChannel,
  IDestination,
  IPolicyApplication, IPremium,
  IProduct,
  IQuotes,
} from '../../../../data/api/models';
import { formValueSelector, InjectedFormProps, submit } from 'redux-form';
import { IStore } from '../../../../redux/IStore';
import { ICompleteData, IQuoteState } from '../../../../services/quote/models';
import { showDestinations, showRegions } from '../../../../data/api/api';
import { bindActionCreators } from 'redux';
import { actions } from '../../../../services/quote/reducer';
import { push } from 'react-router-redux';
import * as Commonmark from 'react-commonmark';
import { REGION_WORLDWIDE_EXCLUDING_LABEL_OLD } from '../../index';
import BusinessContinuityPlan from '../../../../helpers/BusinessContinuityPlan';
import QuoteIcons from '../../components/Icon/QuoteIcons';
import {differenceInDays, endOfDay, parse, startOfDay} from 'date-fns';
import {replaceBenefitTextPlaceHolder} from '../../../../helpers/Quote';
import { FormattedNumber } from 'react-intl';
import DefinitionPopup from '../../../../components/DefinitionPopup';

interface IQuoteSummaryProps extends InjectedFormProps {
  i18n: II18nMessages;
  policyApplication: IPolicyApplication;
  quote: IQuoteState;
  destinations: IDestination[];
  values: IPolicyApplication;
  product: IProduct;
  quotes: IQuotes;
  complete: ICompleteData;
  dispatch: any;
  actions: {
    submit(name: string);
    push: (route: string) => void;
    calculate: any;
  };
  isAggregator: boolean;
  channel: IChannel;
  permissions: boolean;
  quoteType: string;
  selectedPremium: IPremium;
  isAnnual: boolean;

  renderDefinition(name: string, useProvidedString?: string): any;
}

interface IDisplayBenefits {
  text: string;
  name: string;
}

class QuoteSummary extends React.Component<IQuoteSummaryProps, any> {

  private getTripDuration = (startDate, endDate, addDays) => {
    startDate = startOfDay(parse(startDate, 'dd/MM/yyyy', new Date()));
    endDate = endOfDay(parse(endDate, 'dd/MM/yyyy', new Date()));

    if (!addDays) {
      addDays = 0;
    }

    return differenceInDays(endDate, startDate) + addDays;
  }

  private renderOptionIcons = (optionName) => {
    return (<QuoteIcons name={optionName} color={'blue'}/>);
  }

  private renderDestinationRegion = () => {
    const title = this.props.isAnnual ? 'Region' : 'Destinations'

    const { values, product } = this.props;

    let content = (
      <div>Nothing Selected</div>
    );

    if (showDestinations(values.tripType) && values.destinations.length > 0 && values.destinations[0].length > 0) {
      content = (
        <div className={'title'}>
          {values.destinations.map((destination, key) => (<div className={'flex--stretch'} key={key}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'Globe'} color={'blue'}/></div>
              <div className={'traveller-info single'}>
                <div
                  className={'title'}>{destination}</div>
              </div>
            </div>
          </div>))}
        </div>
      );
    }

    if (showRegions(values.tripType) && values.region) {
      let region = product.metadata.regions[values.region];
      if (values.region === 'worldwide_excluding' && this.props.policyApplication.createdBeforeFixedSchemeVersionRule) {
        region = REGION_WORLDWIDE_EXCLUDING_LABEL_OLD;
      }
      content = (
        <div className={'flex--stretch'}>
          <div className={'traveller-info-container'}>
            <div><QuoteIcons name={'Globe'} color={'blue'}/></div>
            <div className={'traveller-info single'}>
              <div
                className={'title'}>{region}</div>
            </div>
          </div>
        </div>
    )
      ;
    }

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{title}</div>
          {content}
        </div>
      </div>
    )
  }

  private renderLevelOfCover = () => {
    const benefitsToDisplay: IDisplayBenefits[] = [
      { text: 'Medical Emergency Cover up to ', name: 'Emergency Medical Costs and Repatriation' },
      { text: 'Cancellation or cutting short your trip up to ', name: 'Cancellation or cutting your trip short' },
      { text: 'Personal Belongings Cover up to ', name: 'Personal belongings' },
      { text: 'Loss of money and documents up to ', name: 'Money, Passports and Travel Documents' }
    ]

    const travelDuration = this.props.isAnnual ? (
      <div className={'cover-level-list'}>
        <QuoteIcons name={'Tick'} color={'blue'}/>
        <div>Unlimited travel for up to 31 days per trip</div>
      </div>
    ) : '';

    let iconColor = 'darkBlue';

    const { quotes } = this.props;
    const coverLevel = Object.keys(quotes)[0];

    switch (coverLevel) {
      case 'gold':
        iconColor = 'gold';
        break;
      case 'platinum':
        iconColor = 'platinum'
        break;
      default:
        break;
    }

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Level of cover'}</div>
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'ShieldTicked'} color={iconColor}/></div>
              <div className={'traveller-info single'}>
                <div className={'title'}>{startCase(`${coverLevel} Cover`)}</div>
              </div>
            </div>
            {travelDuration}

            {map(benefitsToDisplay, (benefit: IDisplayBenefits, key) => {
              const selectedBenefit = find(get(this.props.selectedPremium, 'benefits'), ['name', benefit.name]);
              const displayValue = get(selectedBenefit, `text`, '');
              const text = benefit.text.split('up to')[0] + displayValue.toLowerCase()

              return (
                <div className={'cover-level-list'} key={key}>
                  <QuoteIcons name={'Tick'} color={'blue'}/>
                  <div>{text}</div>
                </div>
              )
            })}

            <div className={'cover-level-list'}>
              <QuoteIcons name={'Tick'} color={'blue'}/>
              <div>{'Legal Liability'}</div>
            </div>

            <div className={'cover-level-list'}>
              <QuoteIcons name={'Tick'} color={'blue'}/>
              <div>{'Legal Protection'}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  private renderExtraCover = () => {
    const { values, product, selectedPremium, quotes } = this.props;
    const options = values.options;
    const coverLevel = Object.keys(quotes)[0];

    const addedOptionNumber = filter(options, (o, key) => {
      return o === true;
    }).length

    const showTitle = addedOptionNumber === 0;

    const additionalCover = {
      cruise_cover: get(product, 'metadata.options.cruise_cover'),
      winter_sports: get(product, 'metadata.options.winter_sports'),
      gadget_cover: get(product, 'metadata.options.gadget_cover'),
      excess_waiver: get(product, 'metadata.options.excess_waiver'),
      golf_cover: get(product, 'metadata.options.golf_cover'),
      enhanced_trip_disruption: get(product, 'metadata.options.enhanced_trip_disruption'),
    };

    return (
      <div className={'quote-summary-card-container'}>
        {showTitle &&
          <div className={'flex-container'}>
            <div className={'flex--20 summary-section-title'}>{'Extra cover'}</div>
            <div className={'flex--stretch'}>
              <div className={'traveller-info-container'}>
                <div><QuoteIcons name={'ShieldPlus'} color={'blue'}/></div>
                <div className={'traveller-info'}>
                  <div className={'title'}>No upgrades added</div>
                </div>
              </div>
            </div>
          </div>
        }

        {map(additionalCover, (option, optionKey) => {
          const isGolfCover = optionKey === 'golf_cover';
          const isGadgetCover = optionKey === 'gadget_cover';
          const isCruiseCover = optionKey === 'cruise_cover';
          const isWinterSportsCover = optionKey === 'winter_sports';
          const isEnhancedTripDisruption = optionKey === 'enhanced_trip_disruption';

          const text = get(product, `metadata.definitions.${optionKey}_alt_title_new`, get(product, `metadata.definitions.${optionKey}_alt_title`))
          let bodyText = ((isGolfCover || isCruiseCover || isWinterSportsCover) && get(product, 'metadata')) ? text : product.metadata.definitions[`${optionKey}_alt_${coverLevel}`];

          if (isWinterSportsCover) {
            const wsBenefit = find(get(selectedPremium, 'information.allBenefits', []), ['name', 'Winter sports'])
            const skyEquipment = get(get(wsBenefit, 'sub_benefits', []).find((sb) => sb.name.includes('Ski equipment')), 'cover_level');
            if (get(skyEquipment, coverLevel, null)) {
              bodyText = bodyText.replace('{alt_value}', get(skyEquipment, coverLevel))
            }
          }

          if (isEnhancedTripDisruption) {
            bodyText = get(product, 'metadata.definitions.enhanced_trip_disruption', '').split('Do you require coverage for these situations?')[0]
          }

          if (isGadgetCover) {
            const extraGadgetText = get(product, `metadata.definitions.${optionKey}_alt_dynamic_new`)
            const dynamicText = replaceBenefitTextPlaceHolder(extraGadgetText, get(selectedPremium, 'information.allBenefits', []), 'gadget_cover', true, coverLevel)
            bodyText = bodyText + '\n\n' + dynamicText;
          }

          if (get(options, optionKey)) {
            // Find the first option that is active, and show title against it.
            const showTitleOnConditionRow = !showTitle && optionKey === Object.keys(additionalCover).find(key => options[key]);
            const totalPrice = get(selectedPremium, `information.boltOns.${optionKey}.total`)
            const currency = get(selectedPremium, `currency`)

            const formatted = (
              <FormattedNumber
                value={totalPrice}
                style="currency"
                currency={currency}
              />
            )

            return (
              <div className={'flex-container'} key={optionKey}>
                <div className={'flex--20 summary-section-title'}>{showTitleOnConditionRow ? 'Extra cover' : ''}</div>
                <div className={'flex--stretch'}>
                  <div className={'traveller-info-container extra-cover'}>
                    <div className={'flex-container'}>
                      <div>{this.renderOptionIcons(optionKey)}</div>
                      <div className={'traveller-info single'}>
                        <div>{option.name}</div>
                        <div className={'sub-title'}>Added for {formatted} total</div>
                      </div>
                    </div>
                    <div className={'info-popup'}>INFO <DefinitionPopup
                      name="benefit-info"
                      definition={bodyText}
                      iconType="info"
                    /></div>

                  </div>
                </div>

              </div>
            )
          }
          return null
        })}

      </div>
    )
  }

  private renderDates = () => {
    const startDate = this.props.values.startDate;
    const endDate = this.props.values.endDate;

    const tripDuration = this.props.isAnnual ? 365 : this.getTripDuration(startDate, endDate, 1);

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Cover start'}</div>
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'Calendar'} color={'blue'}/></div>
              <div className={'traveller-info'}>
                <div className={'title'}>{`${tripDuration} days cover`}</div>
                <div>{`Starting ${startDate}`}</div>
                {!this.props.isAnnual && (
                  <div>{`Ending ${endDate}`}</div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={'flex-container cover-level-list-container'}>
          <div className={'flex--20'}/>
          <div className={'flex--stretch'}>
            <div className={'cover-level-list cancellation-note no-padding'}>
              <div className={'cancellation-note-icon'}><QuoteIcons name={'Tick'} color={'green'}/></div>
              <div
                className={'sub-title'}>{'You are covered for holiday cancellation as soon as your cover starts.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  private renderDocuments = () => {
    const { selectedPremium } = this.props;
    const documentLink = get(selectedPremium, `scheme.documents.Insurance Product Information Document`)

    return (
      <div className={'quote-summary-card-container'}>
        <div className={'flex-container'}>
          <div className={'flex--20 summary-section-title'}>{'Documents'}</div>
          <div className={'flex--stretch'}>
            <div className={'traveller-info-container'}>
              <div><QuoteIcons name={'IPID'} color={'blue'}/></div>
              <div className={'traveller-info'}>
                <div><a href={documentLink} target={'_blank'}>Insurance Product Information Document</a></div>
                <div>{`Contains a summary of your insurance cover`}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  private renderTravellersCard = () => {
    const { values } = this.props;
    const { travellers } = values;
    if (!travellers || !values.tripType) {
      return null;
    }

    return (
      <>
        <div className={'quote-summary-card-container'}>

          <div className={'flex-container'}>
            <div className={'flex--20 summary-section-title'}>Travellers</div>
            <div className={'flex--stretch'}>

              {map(travellers, (traveller, index) => {
                const addressLine1 = traveller?.address?.line1;
                let travellerName = traveller.title ? traveller.title + ' ' : ''
                travellerName += traveller.firstName + ' ' + traveller.lastName
                return (
                  <>
                    <div className={'traveller-info-container'} key={index}>
                      <div><QuoteIcons name={'Person'} color={'blue'}/></div>
                      <div className={'traveller-info'}>
                        <div
                          className={'title'}>{travellerName}</div>
                        <div>{traveller.dob}</div>
                        <div>{addressLine1 ? addressLine1 + ', ' + traveller?.address?.postcode : ''}</div>
                        <div>{traveller?.email}</div>
                        <div>{traveller?.phone}</div>
                        {traveller.screening && (
                          <div className={'traveller-medical-section'}>
                            <div className={'title'}>Medical Conditions</div>
                            <div className="traveller-conditions">{traveller.screening.meta.conditions.join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )
              })}
            </div>
          </div>
        </div>
      </>
    )
  }

  public render() {
    const {values, product, complete, channel} = this.props;

    if (!values || !product) {
      return null;
    }

    const bcp = channel.bcp || null;
    const bcpMessage = BusinessContinuityPlan.getBCPMessage(bcp);

    return (
      <div className={'quote-summary-main'}>
        <Row>
          <Col sm={12}>
            <div className="section-group">
              {(bcpMessage) && (
                <div className={'section-important'}>
                  <div className={'important'}>
                    <Commonmark source={bcpMessage}/>
                  </div>
                </div>
              )}

              <div className={'quote-card-title'} style={{ marginBottom: 0 }}>
                Your Quote Summary
              </div>
              <div className={'section-group-content'}>
                {this.renderTravellersCard()}
                {this.renderDestinationRegion()}
                {this.renderLevelOfCover()}
                {this.renderExtraCover()}
                {this.renderDates()}
                {this.renderDocuments()}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

let selector;

export default flow([
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const {quotes, complete} = state.quote;
      const values = selector(
        state,
        'destinations',
        'startDate',
        'endDate',
        'region',
        'tripType',
        'options',
        'travellers',
        'groupType',
        'schemeId',
        'quoteReference',
      );

      return {
        i18n: state.branding.i18n,
        policyApplication: state.quote.application,
        destinations: state.quote.destinations,
        values,
        quotes,
        complete,
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        submit: bindActionCreators(submit, dispatch),
        calculate: bindActionCreators(actions.calculate, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
])(QuoteSummary);
